import { NavLink, Outlet } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  CircleDollarSign,
  Cloud,
  FileCheck2,
  Gift,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  LifeBuoy,
  Lock,
  LogOut,
  Package,
  Receipt,
  ReceiptText,
  Smartphone,
  ShoppingBag,
  UsersRound,
  Settings as SettingsIcon,
  ShieldCheck,
  Store,
  Tags,
  Truck,
  UserRound,
  Users as UsersIcon,
  Warehouse,
  WifiOff,
  Moon,
  Sun,
} from 'lucide-react';
import { PERMISSIONS } from '@pospak/shared';
import { useAuth } from '../stores/auth';
import { useOfflineSync } from '../lib/use-offline-sync';
import { LicenseLockBanner } from './LicenseLockBanner';
import { useI18n, useT } from '../stores/i18n';
import { useTheme } from '../stores/theme';
import { ShiftBar } from '../features/shifts/ShiftBar';
import { UpdateBanner } from './UpdateBanner';

// Grouped into labeled sections (docs/pospak-business-fit-audit.md's
// Dukaan-POS cross-check flagged the old flat 20-item list as harder to scan
// than a sectioned sidebar). Dashboard stays outside any section — it's the
// one item every role lands on, not part of a workflow group. A section
// with zero visible items (every item filtered out by permission) renders
// nothing, including its own heading — see the filter in AppLayout below.
const NAV_SECTIONS = [
  {
    section: null,
    sectionKey: null,
    items: [{ to: '/dashboard', label: 'Dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, permission: null, end: true }],
  },
  {
    section: 'Operations',
    sectionKey: 'nav.section.operations',
    items: [
      { to: '/sell', label: 'Sell', labelKey: 'nav.sell', icon: Receipt, permission: PERMISSIONS.SALES_CREATE, hideForWarehouse: true },
      { to: '/bills', label: 'Bills', labelKey: 'nav.bills', icon: ReceiptText, permission: PERMISSIONS.SALES_VIEW },
      { to: '/shift', label: 'Shift', labelKey: 'nav.shift', icon: Lock, permission: PERMISSIONS.SHIFT_VIEW, hideForWarehouse: true },
      { to: '/orders', label: 'Orders', labelKey: 'nav.orders', icon: ShoppingBag, permission: PERMISSIONS.ORDERS_VIEW, hideForWarehouse: true, module: 'portal' },
      { to: '/delivery', label: 'Delivery', labelKey: 'nav.delivery', icon: Truck, permission: PERMISSIONS.DELIVERY_VIEW, module: 'delivery' },
      { to: '/customers', label: 'Customers', labelKey: 'nav.customers', icon: UserRound, permission: PERMISSIONS.CUSTOMERS_VIEW },
      { to: '/quotations', label: 'Quotations', labelKey: 'nav.quotations', icon: FileCheck2, permission: PERMISSIONS.QUOTATIONS_VIEW, module: 'quotations' },
      { to: '/bulk-order', label: 'Bulk order', labelKey: 'nav.bulkOrder', icon: LayoutGrid, permission: PERMISSIONS.SALES_CREATE, hideForWarehouse: true, module: 'wholesale' },
      { to: '/vouchers', label: 'Vouchers', labelKey: 'nav.vouchers', icon: Gift, permission: PERMISSIONS.SALES_VOUCHER_MANAGE, module: 'vouchers' },
      { to: '/offline-sync', label: 'Offline sales', labelKey: 'nav.offlineSync', icon: WifiOff, permission: PERMISSIONS.SALES_VIEW, module: 'offline' },
    ],
  },
  {
    section: 'Catalog',
    sectionKey: 'nav.section.catalog',
    items: [
      { to: '/catalog', label: 'Products', labelKey: 'nav.products', icon: Package, permission: PERMISSIONS.CATALOG_VIEW },
      { to: '/pricing', label: 'Price lists', labelKey: 'nav.pricelists', icon: Tags, permission: PERMISSIONS.PRICING_VIEW },
      { to: '/inventory', label: 'Inventory', labelKey: 'nav.inventory', icon: Warehouse, permission: PERMISSIONS.INVENTORY_VIEW },
      { to: '/purchasing', label: 'Purchasing', labelKey: 'nav.purchasing', icon: Truck, permission: PERMISSIONS.PURCHASE_VIEW },
    ],
  },
  {
    section: 'Finance',
    sectionKey: 'nav.section.finance',
    items: [
      { to: '/accounts', label: 'Accounts', labelKey: 'nav.accounts', icon: BookOpen, permission: PERMISSIONS.ACCOUNTS_VIEW },
      { to: '/mobile', label: 'Mobile shop', labelKey: 'nav.mobile', icon: Smartphone, permission: PERMISSIONS.MOBILE_VIEW, module: 'mobile' },
      { to: '/fbr', label: 'FBR invoicing', labelKey: 'nav.fbr', icon: FileCheck2, permission: PERMISSIONS.FBR_VIEW, module: 'fbr' },
      // ANY report permission opens this, not the sales one alone: a
      // storekeeper holds `reports.inventory.view` and HR holds
      // `reports.hr.view`, and both used to lose the whole Reports link —
      // even though `GET /reports` already returns exactly the reports each
      // of them is allowed to run.
      {
        to: '/reports',
        label: 'Reports',
        labelKey: 'nav.reports',
        icon: BarChart3,
        permission: null,
        anyPermission: [
          PERMISSIONS.REPORTS_SALES_VIEW,
          PERMISSIONS.REPORTS_INVENTORY_VIEW,
          PERMISSIONS.REPORTS_TAX_VIEW,
          PERMISSIONS.REPORTS_CASH_VIEW,
          PERMISSIONS.REPORTS_ACCOUNTS_VIEW,
          PERMISSIONS.REPORTS_LOYALTY_VIEW,
          PERMISSIONS.REPORTS_ORDERS_VIEW,
          PERMISSIONS.REPORTS_MOBILE_VIEW,
          PERMISSIONS.REPORTS_HR_VIEW,
        ],
      },
    ],
  },
  {
    section: 'People',
    sectionKey: 'nav.section.people',
    items: [{ to: '/hr', label: 'HR & payroll', labelKey: 'nav.hr', icon: UsersRound, permission: PERMISSIONS.HR_VIEW }],
  },
  {
    section: 'Admin',
    sectionKey: 'nav.section.admin',
    items: [
      { to: '/shops', label: 'Shops', labelKey: 'nav.shops', icon: Store, permission: PERMISSIONS.SHOPS_VIEW },
      { to: '/users', label: 'Users', labelKey: 'nav.users', icon: UsersIcon, permission: PERMISSIONS.USERS_VIEW },
      { to: '/roles', label: 'Roles', labelKey: 'nav.roles', icon: ShieldCheck, permission: PERMISSIONS.ROLES_VIEW },
      { to: '/support', label: 'Support', labelKey: 'nav.support', icon: LifeBuoy, permission: PERMISSIONS.SUPPORT_VIEW },
      // Sync is deliberately absent: pospak is accessed directly against the
      // cloud database, so there is no local server to reconcile with and
      // the diagnostics screen has nothing meaningful to show an operator.
      // The sync module, its tables and its capture triggers are all still
      // in place — the route (`/sync`) still resolves for anyone who types
      // it — only the nav entry is gone. Restore this line if a
      // local-server deployment is ever offered again.
      { to: '/receipt-templates', label: 'Receipt design', labelKey: 'nav.receiptTemplates', icon: LayoutTemplate, permission: PERMISSIONS.SETTINGS_VIEW, module: 'receipt-designer' },
      { to: '/settings', label: 'Settings', labelKey: 'nav.settings', icon: SettingsIcon, permission: PERMISSIONS.SETTINGS_VIEW },
    ],
  },
] as const;

function today(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function ShopPicker() {
  const user = useAuth((s) => s.user);
  const activeShopId = useAuth((s) => s.activeShopId);
  const setActiveShop = useAuth((s) => s.setActiveShop);

  // A single-shop business never sees a picker — that is the whole promise
  // that one product serves a one-counter shop and a chain (docs/SPEC.md §19).
  if (!user || user.shops.length <= 1) {
    const only = user?.shops[0];
    return (
      <span className="text-sm text-ink2">
        {only?.name ?? ''}
        {only?.kind === 'WAREHOUSE' && <span className="ml-1.5 text-xs text-ink3">(Warehouse)</span>}
      </span>
    );
  }

  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">Active shop</span>
      <select
        className="input h-9 w-52"
        value={activeShopId ?? ''}
        onChange={(e) => setActiveShop(e.target.value)}
      >
        {user.shops.map((shop) => (
          <option key={shop.id} value={shop.id}>
            {shop.code} — {shop.name}
            {shop.kind === 'WAREHOUSE' ? ' (Warehouse)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatusBanner() {
  const user = useAuth((s) => s.user);
  if (!user) return null;

  if (user.subscriptionExpired) {
    return (
      <div className="bg-danger px-4 py-2 text-center text-sm text-white">
        Your subscription has expired — you can view everything but cannot make changes.
      </div>
    );
  }
  if (user.cloudReadOnly) {
    return (
      <div className="flex items-center justify-center gap-2 bg-info px-4 py-2 text-center text-sm text-white">
        <Cloud size={16} aria-hidden />
        You're viewing the cloud copy. Selling and stock changes happen at the shop.
      </div>
    );
  }
  return null;
}

/** Offline sell queue status (lib/use-offline-sync.ts) — visible on every
 * screen, same "always show connection state" idea Dukaan-POS's offline
 * badge/banner does, scoped to what pospak's queue actually covers (see
 * lib/offline-queue.ts's header for the exact boundary). */
function OfflineBanner() {
  const { isOnline, pending, failed } = useOfflineSync();
  if (isOnline && pending === 0 && failed === 0) return null;

  return (
    <div
      className={`flex items-center justify-center gap-2 px-4 py-2 text-center text-sm text-white ${
        failed > 0 ? 'bg-danger' : isOnline ? 'bg-warn' : 'bg-danger'
      }`}
    >
      <WifiOff size={16} aria-hidden />
      <span>
        {isOnline
          ? pending > 0
            ? `Back online — syncing ${pending} item${pending === 1 ? '' : 's'}…`
            : ''
          : pending > 0
            ? `Offline — ${pending} item${pending === 1 ? '' : 's'} will sync when connection returns`
            : 'Offline — new sales and lines may fail until connection returns'}
        {failed > 0 && (
          <>
            {(pending > 0 || !isOnline) && ' · '}
            {failed} item{failed === 1 ? '' : 's'} couldn't sync —{' '}
            <NavLink to="/offline-sync" className="underline">
              review
            </NavLink>
          </>
        )}
      </span>
    </div>
  );
}

/** EN/UR toggle (task #36) — deliberately tiny, lives in the header next to
 * the date rather than a whole "settings" flow, since it's a one-tap
 * preference a cashier reaches for constantly, not a rarely-touched setting. */
/** Light/dark switch — the dark tokens shipped with the design system; this
 * is the delivery mechanism they never had. */
function ThemeToggle() {
  const theme = useTheme((s) => s.theme);
  const toggle = useTheme((s) => s.toggle);
  return (
    <button
      type="button"
      className="btn-ghost h-8 px-2 text-xs"
      onClick={toggle}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {theme === 'dark' ? <Sun size={15} aria-hidden /> : <Moon size={15} aria-hidden />}
    </button>
  );
}

function LanguageToggle() {
  const lang = useI18n((s) => s.lang);
  const setLang = useI18n((s) => s.setLang);
  return (
    <button
      type="button"
      className="btn-ghost h-8 px-2 text-xs"
      onClick={() => setLang(lang === 'ur' ? 'en' : 'ur')}
      title="Switch language / زبان تبدیل کریں"
    >
      {lang === 'ur' ? 'English' : 'اردو'}
    </button>
  );
}

export function AppLayout() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const hasPermission = useAuth((s) => s.hasPermission);
  const modulesEnabled = useAuth((s) => s.user?.modulesEnabled ?? []);
  const activeShopId = useAuth((s) => s.activeShopId);
  const activeShopKind = user?.shops.find((s) => s.id === activeShopId)?.kind;
  const t = useT();

  return (
    <div className="flex h-full">
      <nav className="no-print flex w-56 shrink-0 flex-col border-r border-border bg-surface">
        <div className="flex items-center gap-2 px-4 py-4">
          <CircleDollarSign className="text-brand" size={22} aria-hidden />
          <span className="font-display text-lg font-bold">Pospak</span>
        </div>

        <div className="px-4 pb-3">
          <p className="truncate text-sm font-medium">{user?.businessName}</p>
          <p className="num truncate text-xs text-ink3">{user?.businessCode}</p>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-2 pb-2">
          {NAV_SECTIONS.map((group) => {
            const items = group.items.filter((item) => {
              if (item.permission !== null && !hasPermission(item.permission)) return false;
              if ('anyPermission' in item && !item.anyPermission.some((p) => hasPermission(p))) return false;
              if ('module' in item && !modulesEnabled.includes(item.module)) return false;
              if ('hideForWarehouse' in item && item.hideForWarehouse && activeShopKind === 'WAREHOUSE') return false;
              return true;
            });
            if (items.length === 0) return null;
            // Widened to plain `string | null` — otherwise TS ties `section`
            // and `sectionKey` together as correlated literals from the
            // `as const` tuple and (wrongly, since they're independent
            // fields) narrows one branch below to `never`.
            const section: string | null = group.section;
            const sectionKey: string | null = group.sectionKey;
            return (
              <div key={section ?? '__top'}>
                {section && (
                  <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink3">
                    {sectionKey ? t(sectionKey, section) : section}
                  </p>
                )}
                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={'end' in item ? item.end : false}
                        className={({ isActive }) =>
                          [
                            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                            isActive
                              ? 'bg-brand text-brandInk font-medium'
                              : 'text-ink2 hover:bg-surface2 hover:text-ink',
                          ].join(' ')
                        }
                      >
                        <item.icon size={18} aria-hidden />
                        {t(item.labelKey, item.label)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="border-t border-border p-2">
          <div className="px-2 py-2">
            <p className="truncate text-sm font-medium">{user?.fullName}</p>
            <p className="truncate text-xs text-ink3">{user?.roles.join(', ')}</p>
          </div>
          <NavLink
            to="/change-password"
            className={({ isActive }) =>
              [
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                isActive ? 'bg-brand text-brandInk font-medium' : 'text-ink2 hover:bg-surface2 hover:text-ink',
              ].join(' ')
            }
          >
            <KeyRound size={16} aria-hidden />
            {t('chrome.changePassword', 'Change password')}
          </NavLink>
          <button type="button" className="btn-ghost w-full" onClick={() => void logout()}>
            <LogOut size={16} aria-hidden />
            {t('chrome.signOut', 'Sign out')}
          </button>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <StatusBanner />
        <LicenseLockBanner />
        <UpdateBanner />
        <OfflineBanner />
        <header className="no-print flex h-14 items-center justify-between gap-4 border-b border-border bg-surface px-6">
          <ShopPicker />
          <div className="flex items-center gap-3 text-sm text-ink3">
            <ShiftBar />
            <ThemeToggle />
            <LanguageToggle />
            <span className="num">{today()}</span>
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
